import React, { useCallback, useState } from 'react';

import Button from '@components/Button';
import BylineWithHeadshot from '@components/BylineWithHeadshot';
import IconArrowDown from '@components/Images/Rebranding/IconArrowDown';
import { useAppContext } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import { Author } from '@interfaces/authors';
import { ClickTarget, click } from '@tracking';

import ArticleCommentJumpLink from './ArticleCommentJumpLink';
import ShareArticleModal from './ShareArticleModal';

interface DetailsProps {
  authors: Author[];
  commentCount?: number;
  commenters?: string[];
  publishedAt: string;
  shareLink?: string;
}

const STICKY_NAV_PLUS_SPACE = 100;

const ArticleDetails = ({
  authors,
  commentCount = 0,
  commenters = [],
  publishedAt,
  shareLink,
}: DetailsProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const { isWebview } = useAppContext();
  const { trackEvent } = useEventTracking();

  const handleShareClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.ShareButton }));
    setIsShareModalOpen(true);
  };

  const handleCommentJumpClick = useCallback(() => {
    const commentsSection = document.getElementById('comments-section');
    if (!commentsSection) return;
    const scrollTopDistance = commentsSection.getBoundingClientRect().top;
    window?.scrollBy({
      behavior: 'smooth',
      left: 0,
      top: scrollTopDistance - STICKY_NAV_PLUS_SPACE,
    });
  }, []);

  return (
    <>
      <div className="flex items-end border-0 border-b border-solid border-grayscale-gray pb-24 sm:items-center">
        {authors.length > 0 && <BylineWithHeadshot authors={authors} className="max-w-sm" />}
        {shareLink && !isWebview && (
          <Button
            as="button"
            className="share-button ml-auto flex items-center border-0 bg-grayscale-gray px-16 py-8 font-suisse-intl-mono text-button-xs text-white outline-none"
            onClick={handleShareClick}
          >
            Share
            <IconArrowDown className="ml-10 w-20 fill-white" />
          </Button>
        )}
      </div>
      <div className="flex items-end justify-end md:justify-between">
        <time
          className="mb-0 mt-8 block font-suisse-intl-mono text-overline-md text-grayscale-gray lg:text-overline-sm xl:text-overline-md"
          dateTime={publishedAt}
        >
          {publishedAt}
        </time>
        {commentCount > 0 && (
          <ArticleCommentJumpLink
            commentCount={commentCount}
            commenters={commenters}
            onClick={handleCommentJumpClick}
          />
        )}
      </div>
      {shareLink && !isWebview && (
        <ShareArticleModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          shareLink={shareLink}
        />
      )}
    </>
  );
};

export default ArticleDetails;
